/**
 * Modelo da Home — tudo derivado, nada constante.
 *
 * Regra de genericidade (ROADMAP §1): duração, número do dia, dias restantes
 * e fase saem das DATAS da viagem, na timezone dela. Nenhum valor da viagem de
 * referência atravessa este arquivo.
 */

import {
  calendarDate,
  daysUntilStart,
  formatDateRange,
  type TripDates,
  tripDayNumber,
  tripDurationInDays,
  type TripPhase,
  tripPhase,
  resolveToday,
} from '@nv/core';

export type DataState = 'ready' | 'loading' | 'empty' | 'error';

export interface TripHeader {
  name: string;
  destination: string;
  /** "27 OUT → 18 NOV" */
  range: string;
  /** Derivado. Nunca lido de constante. */
  durationInDays: number;
  phase: TripPhase;
  /** Só na fase "antes". */
  daysUntilStart: number | null;
  /** Só na fase "durante". */
  dayNumber: number | null;
  participantCount: number;
}

export interface TripInput extends TripDates {
  name: string;
  destination: string;
  participantCount: number;
}

/**
 * Deriva tudo que o cabeçalho da Home mostra.
 *
 * `now` é injetável para que a fase possa ser exercitada em teste e no painel
 * de demonstração sem depender do relógio da máquina.
 */
export function buildTripHeader(trip: TripInput, now: Date = new Date()): TripHeader {
  const phase = tripPhase(trip, now);
  const hoje = resolveToday(trip.timeZone, now, trip.utcOffsetSchedule);

  return {
    name: trip.name,
    destination: trip.destination,
    range: formatDateRange(trip.startDate, trip.endDate),
    durationInDays: tripDurationInDays(trip),
    phase,
    daysUntilStart: phase === 'antes' ? daysUntilStart(trip, now) : null,
    dayNumber: phase === 'durante' ? tripDayNumber(trip, hoje) : null,
    participantCount: trip.participantCount,
  };
}

/**
 * Instante que coloca a viagem numa fase escolhida.
 *
 * Existe para o painel de demonstração e para os testes: o protótipo aprovado
 * tem um seletor de fase, e reproduzi-lo exige poder mover o "hoje" sem mexer
 * nas datas da viagem — que é justamente o que a regra de genericidade proíbe.
 */
export function instantForPhase(trip: TripDates, phase: TripPhase): Date {
  const dia =
    phase === 'antes'
      ? shiftDays(trip.startDate, -10)
      : phase === 'durante'
        ? midpoint(trip.startDate, trip.endDate)
        : shiftDays(trip.endDate, 5);

  // Meio-dia UTC evita que o deslocamento de fuso jogue a data para o dia
  // vizinho em qualquer timezone do mundo.
  return new Date(`${dia}T12:00:00Z`);
}

function shiftDays(date: string, days: number): string {
  const base = new Date(`${date}T00:00:00Z`);
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
}

function midpoint(start: string, end: string): string {
  const a = new Date(`${start}T00:00:00Z`).getTime();
  const b = new Date(`${end}T00:00:00Z`).getTime();
  return new Date((a + b) / 2).toISOString().slice(0, 10);
}

export { calendarDate };
